import { createContext, useContext, useEffect, useState, useRef, useCallback, type ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { Session } from "@supabase/supabase-js";
import React from "react";
import { useLocation } from "react-router-dom";
import { markMarketingAudience } from "@/lib/marketingAudience";

// NOTE: The DB app_role enum also includes "user" but it is intentionally unused
// in the application. It remains in the enum to avoid a destructive migration.
export type PortalRole = "admin" | "consultant" | "installer" | "customer" | "partner" | "marketing_manager" | "introducer" | "introducer_manager" | "sales_manager" | "customer_service" | "surveyor" | null;

interface RoleRow {
  role: string;
  is_primary: boolean;
}

interface PortalRoleState {
  session: Session | null;
  /** The currently-active role (used by all existing checks). */
  role: PortalRole;
  /** All roles assigned to the current user. */
  roles: PortalRole[];
  /** The user's real primary/highest role from user_roles (ignores view-as). */
  primaryRealRole: PortalRole;
  /** True for every explicit admin role preview, including assigned roles. */
  isViewAs: boolean;
  loading: boolean;
  /**
   * Set when the role lookup itself failed. A failed lookup is NEVER the same
   * as "this person has no roles": callers must not grant or deny access on it.
   */
  rolesError: string | null;
  /**
   * Set when the session check itself failed (rejected or returned an error).
   * This is NOT the same as a confirmed signed-out state: nothing may be
   * denied, cleared or signed out because of it.
   */
  sessionError: string | null;
  /** Re-run the session + role lookup (used by retry actions). */
  refreshRoles: () => Promise<void>;
  userId: string | null;
  /** Switch the active role context without re-authenticating. Admins may pass any internal role. */
  setActiveRole: (r: PortalRole) => void;
  /** Clear any view-as and return an admin to their real role. */
  exitViewAs: () => void;
}

const VALID_ROLES: string[] = ["admin", "consultant", "installer", "customer", "partner", "marketing_manager", "introducer", "introducer_manager", "sales_manager", "customer_service", "surveyor"];

// Roles an admin is allowed to enter "view as" mode for.
// "customer" is intentionally excluded — admins must use a real test account for homeowner privacy.
export const ADMIN_VIEW_AS_ROLES: Exclude<PortalRole, null>[] = [
  "admin",
  "introducer_manager",
  "introducer",
  "consultant",
  "sales_manager",
  "customer_service",
  "surveyor",
  "installer",
  "partner",
  "marketing_manager",
];

const VIEW_AS_STORAGE_KEY = "evo.viewAsRole";

function readStoredViewAs(): PortalRole {
  if (typeof window === "undefined") return null;
  try {
    const v = window.sessionStorage.getItem(VIEW_AS_STORAGE_KEY);
    if (v && (ADMIN_VIEW_AS_ROLES as string[]).includes(v)) return v as PortalRole;
  } catch {
    /* ignore */
  }
  return null;
}

function writeStoredViewAs(r: PortalRole) {
  if (typeof window === "undefined") return;
  try {
    if (r) window.sessionStorage.setItem(VIEW_AS_STORAGE_KEY, r);
    else window.sessionStorage.removeItem(VIEW_AS_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

const ROLE_PATH_MATCHERS: Array<{ prefix: string; role: Exclude<PortalRole, null> }> = [
  { prefix: "/portal/admin", role: "admin" },
  { prefix: "/portal/consultant", role: "consultant" },
  { prefix: "/portal/installer", role: "installer" },
  { prefix: "/portal/partner", role: "partner" },
  { prefix: "/portal/marketing", role: "marketing_manager" },
  { prefix: "/portal/introducer-manager", role: "introducer_manager" },
  { prefix: "/portal/introducer", role: "introducer" },
  { prefix: "/portal/sales", role: "sales_manager" },
  { prefix: "/customer", role: "customer" },
  { prefix: "/portal/customer-service", role: "customer_service" },
  { prefix: "/portal/surveyor", role: "surveyor" },
];

const PortalRoleContext = createContext<PortalRoleState | undefined>(undefined);

function resolveRoleFromPathname(pathname: string): PortalRole {
  const match = ROLE_PATH_MATCHERS.find(({ prefix }) => pathname === prefix || pathname.startsWith(`${prefix}/`));
  return match?.role ?? null;
}

function pickPreferredRole(
  pathname: string,
  assignedRoles: PortalRole[],
  primaryRole: PortalRole,
  currentRole: PortalRole,
): PortalRole {
  const routeRole = resolveRoleFromPathname(pathname);
  // Opening a shared tool must not silently change the owner workspace.
  if (assignedRoles.includes("admin") && routeRole !== "customer") return "admin";
  if (routeRole && assignedRoles.includes(routeRole)) {
    return routeRole;
  }

  if ((pathname === "/portal" || pathname === "/portal/") && assignedRoles.length > 1) {
    const firstPortalRole = assignedRoles.find((candidate) => candidate !== null && candidate !== "customer");
    if (firstPortalRole) {
      return firstPortalRole;
    }
  }

  if (currentRole && assignedRoles.includes(currentRole)) {
    return currentRole;
  }

  if (primaryRole && assignedRoles.includes(primaryRole)) {
    return primaryRole;
  }

  return assignedRoles[0] ?? null;
}

export interface RoleFetchResult {
  roles: PortalRole[];
  primary: PortalRole;
  /** Non-null when the lookup failed. An empty result with no error is a real "no roles". */
  error: string | null;
}

const ROLE_LOOKUP_FAILED = "Your access could not be checked just now.";
const SESSION_CHECK_FAILED = "Your sign-in could not be checked just now.";

async function fetchRolesForUser(userId: string): Promise<RoleFetchResult> {
  let data: unknown;
  let error: { message?: string } | null = null;
  try {
    ({ data, error } = await supabase
      .from("user_roles")
      .select("role, is_primary")
      .eq("user_id", userId)
      .in("role", VALID_ROLES as ("admin" | "consultant" | "installer" | "customer" | "partner" | "marketing_manager" | "introducer" | "introducer_manager" | "sales_manager" | "customer_service" | "surveyor" | "user")[]));
  } catch (caught) {
    // A rejected lookup must settle as a failure, never as "no roles".
    return { roles: [], primary: null, error: caught instanceof Error ? caught.message : ROLE_LOOKUP_FAILED };
  }

  if (error) {
    return { roles: [], primary: null, error: error.message || ROLE_LOOKUP_FAILED };
  }

  if (!Array.isArray(data)) {
    return { roles: [], primary: null, error: ROLE_LOOKUP_FAILED };
  }

  const rows = data as RoleRow[];
  const roles = rows.map((r) => r.role as PortalRole).filter((r) => r !== null);
  const primaryRow = rows.find((r) => r.is_primary);
  const primary = (primaryRow?.role as PortalRole) ?? roles[0] ?? null;

  return { roles, primary, error: null };
}

/**
 * Provider that creates a SINGLE auth subscription and role fetch,
 * shared by all components that call usePortalRole().
 */
export function PortalRoleProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const [session, setSession] = useState<Session | null>(null);
  const [roles, setRoles] = useState<PortalRole[]>([]);
  const [primaryRealRole, setPrimaryRealRole] = useState<PortalRole>(null);
  const [activeRole, setActiveRoleState] = useState<PortalRole>(null);
  const [viewAsRole, setViewAsRoleState] = useState<PortalRole>(() => readStoredViewAs());
  const [loading, setLoading] = useState(true);
  const [rolesError, setRolesError] = useState<string | null>(null);
  const [sessionError, setSessionError] = useState<string | null>(null);
  const mountedRef = useRef(true);
  const pathnameRef = useRef(location.pathname);
  const resolvedUserIdRef = useRef<string | null>(null);
  /**
   * Monotonic generation for every session/role request. Any answer whose
   * generation is no longer the current one belongs to a superseded account,
   * retry or sign-out and must be discarded.
   */
  const generationRef = useRef(0);
  const syncRef = useRef<((next: Session | null, generation?: number) => Promise<void>) | null>(null);

  const isAdmin = roles.includes("admin");

  const setActiveRole = useCallback((r: PortalRole) => {
    if (!r) return;
    if (isAdmin && r !== "admin" && (ADMIN_VIEW_AS_ROLES as string[]).includes(r)) {
      setViewAsRoleState(r);
      writeStoredViewAs(r);
      setActiveRoleState(r);
      return;
    }
    if (roles.includes(r)) {
      // Real role of the user — clear any view-as
      setViewAsRoleState(null);
      writeStoredViewAs(null);
      setActiveRoleState(r);
      return;
    }
    if (isAdmin && (ADMIN_VIEW_AS_ROLES as string[]).includes(r)) {
      setViewAsRoleState(r);
      writeStoredViewAs(r);
      setActiveRoleState(r);
    }
  }, [roles, isAdmin]);

  const exitViewAs = useCallback(() => {
    setViewAsRoleState(null);
    writeStoredViewAs(null);
    if (isAdmin) setActiveRoleState("admin");
    else if (primaryRealRole) setActiveRoleState(primaryRealRole);
  }, [isAdmin, primaryRealRole]);

  useEffect(() => {
    pathnameRef.current = location.pathname;
  }, [location.pathname]);

  useEffect(() => {
    mountedRef.current = true;

    const syncSessionAndRoles = async (
      nextSession: Session | null,
      generation: number = ++generationRef.current,
    ) => {
      const isCurrent = () => mountedRef.current && generationRef.current === generation;
      if (!isCurrent()) return;

      setSession(nextSession);
      setSessionError(null);
      const nextUserId = nextSession?.user?.id ?? null;

      if (!nextUserId) {
        resolvedUserIdRef.current = null;
        setRoles([]);
        setPrimaryRealRole(null);
        setActiveRoleState(null);
        setViewAsRoleState(null);
        writeStoredViewAs(null);
        setRolesError(null);
        setLoading(false);
        return;
      }

      // Token refreshes fire when a browser/tab returns to the foreground.
      // For the already-resolved user, update the token without putting the
      // protected route back into its loading screen (which unmounts editors).
      if (resolvedUserIdRef.current === nextUserId) {
        return;
      }

      // A different account is arriving: nothing granted to the previous
      // identity may survive even for a moment.
      if (resolvedUserIdRef.current !== null) {
        setRoles([]);
        setPrimaryRealRole(null);
        setActiveRoleState(null);
      }
      resolvedUserIdRef.current = null;

      setLoading(true);
      setRolesError(null);

      const { roles: nextRoles, primary, error } = await fetchRolesForUser(nextUserId);
      // Staff activity must not be counted as homeowner marketing traffic.
      if (!error) markMarketingAudience(nextRoles);
      // Drop superseded answers: another account, retry or sign-out has won.
      if (!isCurrent()) return;

      if (error) {
        // A failed check is not an empty result: keep nothing granted, report it.
        setRoles([]);
        setPrimaryRealRole(null);
        setActiveRoleState(null);
        setRolesError(error);
        setLoading(false);
        return;
      }

      setRoles(nextRoles);
      setPrimaryRealRole(primary);
      setRolesError(null);
      resolvedUserIdRef.current = nextUserId;
      const stored = readStoredViewAs();
      const nextIsAdmin = nextRoles.includes("admin");
      if (stored && nextIsAdmin && (ADMIN_VIEW_AS_ROLES as string[]).includes(stored)) {
        setViewAsRoleState(stored);
        setActiveRoleState(stored);
      } else {
        setViewAsRoleState(null);
        writeStoredViewAs(null);
        setActiveRoleState((currentRole) =>
          pickPreferredRole(pathnameRef.current, nextRoles, primary, currentRole)
        );
      }
      setLoading(false);
    };

    syncRef.current = syncSessionAndRoles;

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      // Claim the generation immediately, so any request already in flight
      // (including the first load) can no longer apply its answer.
      const generation = ++generationRef.current;
      setTimeout(() => {
        void syncSessionAndRoles(nextSession, generation);
      }, 0);
    });

    const init = async () => {
      const generation = ++generationRef.current;
      try {
        const { data, error } = await supabase.auth.getSession();
        // A newer auth event has already taken over: discard this answer.
        if (!mountedRef.current || generationRef.current !== generation) return;
        if (error) {
          // Unknown, not signed out: nothing is cleared or denied here.
          setSessionError(error.message || SESSION_CHECK_FAILED);
          setLoading(false);
          return;
        }
        await syncSessionAndRoles(data?.session ?? null, generation);
      } catch (caught) {
        if (!mountedRef.current || generationRef.current !== generation) return;
        setSessionError(caught instanceof Error ? caught.message : SESSION_CHECK_FAILED);
        setLoading(false);
      } finally {
        if (mountedRef.current && generationRef.current === generation) setLoading(false);
      }
    };

    void init();

    return () => {
      mountedRef.current = false;
      // Nothing still in flight may apply after unmount.
      generationRef.current += 1;
      subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (loading || roles.length === 0) return;
    // Skip path-driven role recalculation when admin is in view-as mode —
    // their chosen role should drive navigation, not the URL.
    if (viewAsRole) return;

    setActiveRoleState((currentRole) => {
      const nextRole = pickPreferredRole(location.pathname, roles, currentRole, currentRole);
      return nextRole === currentRole ? currentRole : nextRole;
    });
  }, [location.pathname, loading, roles, viewAsRole]);

  const refreshRoles = useCallback(async () => {
    // This retry supersedes anything still in flight.
    const generation = ++generationRef.current;
    let nextSession: Session | null = null;
    try {
      const { data, error } = await supabase.auth.getSession();
      if (!mountedRef.current || generationRef.current !== generation) return;
      if (error) {
        setSessionError(error.message || SESSION_CHECK_FAILED);
        setLoading(false);
        return;
      }
      nextSession = data?.session ?? null;
    } catch (caught) {
      if (mountedRef.current && generationRef.current === generation) {
        setSessionError(caught instanceof Error ? caught.message : SESSION_CHECK_FAILED);
        setLoading(false);
      }
      return;
    }
    // Force a fresh lookup even when the same user is already resolved.
    resolvedUserIdRef.current = null;
    setSessionError(null);
    await syncRef.current?.(nextSession, generation);
  }, []);

  const value: PortalRoleState = {
    session,
    role: activeRole,
    roles,
    primaryRealRole,
    isViewAs: isAdmin && Boolean(viewAsRole) && viewAsRole !== "admin",
    loading,
    rolesError,
    sessionError,
    refreshRoles,
    userId: session?.user?.id ?? null,
    setActiveRole,
    exitViewAs,
  };

  return React.createElement(PortalRoleContext.Provider, { value }, children);
}

/**
 * Hook to access portal role state.
 * When used inside PortalRoleProvider, returns shared state (single subscription).
 * When used outside (e.g. header), creates its own independent state as fallback.
 */
export function usePortalRole(): PortalRoleState {
  const ctx = useContext(PortalRoleContext);
  // Always called (stable hook order). Inside the provider it stays idle:
  // no auth subscription, no session or role reads.
  const standalone = useStandalonePortalRole(ctx === undefined);
  return ctx !== undefined ? ctx : standalone;
}

export function useStandalonePortalRole(enabled = true): PortalRoleState {
  const [session, setSession] = useState<Session | null>(null);
  const [roles, setRoles] = useState<PortalRole[]>([]);
  const [primaryRealRole, setPrimaryRealRole] = useState<PortalRole>(null);
  const [activeRole, setActiveRoleState] = useState<PortalRole>(null);
  const [loading, setLoading] = useState(true);
  const [rolesError, setRolesError] = useState<string | null>(null);
  const [sessionError, setSessionError] = useState<string | null>(null);
  const mountedRef = useRef(true);
  const resolvedUserIdRef = useRef<string | null>(null);
  const generationRef = useRef(0);
  const syncRef = useRef<((next: Session | null, generation?: number) => Promise<void>) | null>(null);

  const setActiveRole = useCallback((r: PortalRole) => {
    if (r && roles.includes(r)) {
      setActiveRoleState(r);
    }
  }, [roles]);

  const exitViewAs = useCallback(() => {
    if (primaryRealRole) setActiveRoleState(primaryRealRole);
  }, [primaryRealRole]);

  useEffect(() => {
    if (!enabled) return;
    mountedRef.current = true;

    const syncSessionAndRoles = async (
      nextSession: Session | null,
      generation: number = ++generationRef.current,
    ) => {
      const isCurrent = () => mountedRef.current && generationRef.current === generation;
      if (!isCurrent()) return;

      setSession(nextSession);
      setSessionError(null);
      const nextUserId = nextSession?.user?.id ?? null;

      if (!nextUserId) {
        resolvedUserIdRef.current = null;
        setRoles([]);
        setPrimaryRealRole(null);
        setActiveRoleState(null);
        setRolesError(null);
        setLoading(false);
        return;
      }

      // Token refresh for the already-resolved user: keep the tree mounted.
      if (resolvedUserIdRef.current === nextUserId) {
        return;
      }

      if (resolvedUserIdRef.current !== null) {
        setRoles([]);
        setPrimaryRealRole(null);
        setActiveRoleState(null);
      }
      resolvedUserIdRef.current = null;

      setLoading(true);
      setRolesError(null);

      const { roles: nextRoles, primary, error } = await fetchRolesForUser(nextUserId);
      // Staff activity must not be counted as homeowner marketing traffic.
      if (!error) markMarketingAudience(nextRoles);
      if (!isCurrent()) return;

      if (error) {
        setRoles([]);
        setPrimaryRealRole(null);
        setActiveRoleState(null);
        setRolesError(error);
        setLoading(false);
        return;
      }

      setRoles(nextRoles);
      setPrimaryRealRole(primary);
      setRolesError(null);
      resolvedUserIdRef.current = nextUserId;
      setActiveRoleState(primary);
      setLoading(false);
    };

    syncRef.current = syncSessionAndRoles;

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      const generation = ++generationRef.current;
      setTimeout(() => {
        void syncSessionAndRoles(nextSession, generation);
      }, 0);
    });

    const init = async () => {
      const generation = ++generationRef.current;
      try {
        const { data, error } = await supabase.auth.getSession();
        if (!mountedRef.current || generationRef.current !== generation) return;
        if (error) {
          setSessionError(error.message || SESSION_CHECK_FAILED);
          setLoading(false);
          return;
        }
        await syncSessionAndRoles(data?.session ?? null, generation);
      } catch (caught) {
        if (!mountedRef.current || generationRef.current !== generation) return;
        setSessionError(caught instanceof Error ? caught.message : SESSION_CHECK_FAILED);
        setLoading(false);
      } finally {
        if (mountedRef.current && generationRef.current === generation) setLoading(false);
      }
    };

    void init();

    return () => {
      mountedRef.current = false;
      // Nothing still in flight may apply after unmount.
      generationRef.current += 1;
      subscription.unsubscribe();
    };
  }, [enabled]);

  const refreshRoles = useCallback(async () => {
    if (!enabled) return;
    const generation = ++generationRef.current;
    let nextSession: Session | null = null;
    try {
      const { data, error } = await supabase.auth.getSession();
      if (!mountedRef.current || generationRef.current !== generation) return;
      if (error) {
        setSessionError(error.message || SESSION_CHECK_FAILED);
        setLoading(false);
        return;
      }
      nextSession = data?.session ?? null;
    } catch (caught) {
      if (mountedRef.current && generationRef.current === generation) {
        setSessionError(caught instanceof Error ? caught.message : SESSION_CHECK_FAILED);
        setLoading(false);
      }
      return;
    }
    resolvedUserIdRef.current = null;
    setSessionError(null);
    await syncRef.current?.(nextSession, generation);
  }, [enabled]);

  return {
    session,
    role: activeRole,
    roles,
    primaryRealRole,
    isViewAs: false,
    loading,
    rolesError,
    sessionError,
    refreshRoles,
    userId: session?.user?.id ?? null,
    setActiveRole,
    exitViewAs,
  };
}
